import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RouteMap } from '../components/RouteMap';
import { Badge, Card, EmptyState, Field, Loading, Meter, Modal, ModuleHeader, SearchSelect, StatCard, Tabs, useAsync, useToast } from '../components/ui';
import {
  IconBuilding,
  IconCheck,
  IconClock,
  IconLink,
  IconNavigation,
  IconChevronDown,
  IconPin,
  IconPlus,
  IconRoute,
  IconSave,
  IconSearch,
  IconSpark,
  IconTrash,
  IconTruck,
  IconX,
} from '../components/icons';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDuration, formatNumber, formatTime } from '../lib/format';

/* ------------------------------------------------------------------ */
/* Typen                                                               */
/* ------------------------------------------------------------------ */

interface PlannerStop {
  key: string;
  label: string;
  address: string | null;
  lat: number;
  lng: number;
  kind: 'start' | 'task' | 'property' | 'address' | 'end';
  taskId?: number | null;
  propertyId?: number | null;
  serviceMinutes?: number | null;
  isStart?: boolean;
  isEnd?: boolean;
  /** Nur zur Anzeige – wird nicht an die Optimierung übergeben. */
  meta?: string;
  criticalityColor?: string | null;
  criticalityName?: string | null;
  /** Herkunft aus einem Servicestandort – erlaubt automatische Vorbelegung. */
  locationId?: number | null;
}

const LOCATION_LABEL: Record<string, string> = {
  depot: 'Betriebshof',
  warehouse: 'Lager',
  office: 'Büro',
  other: 'Standort',
};

interface OptimizedStop extends PlannerStop {
  position: number;
  distanceFromPrev: number;
  driveMinutesFromPrev: number;
  plannedArrival: string | null;
  plannedDeparture: string | null;
}

interface OptimizeResult {
  stops: OptimizedStop[];
  totalDistanceKm: number;
  totalDriveMinutes: number;
  totalServiceMinutes: number;
  totalDurationMinutes: number;
  returnLeg: { distanceKm: number; driveMinutes: number } | null;
  mapsUrl: string;
}

type Mode = 'round_trip' | 'open_end' | 'fixed_end';

const MODE_LABELS: Record<Mode, string> = {
  round_trip: 'Rundtour',
  open_end: 'Offenes Ende',
  fixed_end: 'Festes Ziel',
};

const today = () => new Date().toISOString().slice(0, 10);

let stopCounter = 0;
const nextKey = (prefix: string) => `${prefix}-${(stopCounter += 1)}`;

/* ------------------------------------------------------------------ */
/* Seite                                                               */
/* ------------------------------------------------------------------ */

/**
 * Tourenplanung (Kap. 6).
 *
 * Aus offenen Aufträgen entsteht eine Tagestour: Stopps sammeln, Reihenfolge
 * serverseitig optimieren, Ergebnis prüfen, speichern und an den Außendienst
 * übergeben. Beim Übergeben werden Zuweisung und Zeitfenster der Aufgaben
 * gesetzt – die Tour ist damit Teil der Disposition, kein Nebenwerkzeug.
 */
export function RoutePlannerPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { notify } = useToast();

  const [view, setView] = useState<'planner' | 'saved'>('planner');

  const [name, setName] = useState(`Tour ${today()}`);
  const [planDate, setPlanDate] = useState(today());
  const [assigneeId, setAssigneeId] = useState<number | ''>('');
  const [startTime, setStartTime] = useState('07:30');
  const [avgSpeed, setAvgSpeed] = useState(32);
  const [serviceMinutes, setServiceMinutes] = useState(30);

  /**
   * Zielpunkt der Tour. Er bestimmt zugleich die Streckenführung – ein
   * getrennter Schalter dafür wäre eine Verdopplung derselben Entscheidung:
   *   'start'  zurück zum Startpunkt (Rundtour)
   *   'open'   offenes Ende, das Ziel ergibt sich aus der Optimierung
   *   sonst    fester Zielpunkt (Standort oder freie Adresse)
   */
  const [endChoice, setEndChoice] = useState<'start' | 'open' | 'fixed'>('start');
  const mode: Mode = endChoice === 'start' ? 'round_trip' : endChoice === 'open' ? 'open_end' : 'fixed_end';

  const [stops, setStops] = useState<PlannerStop[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [result, setResult] = useState<OptimizeResult | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskPickerOpen, setTaskPickerOpen] = useState(false);
  const [savedPlanId, setSavedPlanId] = useState<number | null>(null);

  const users = useAsync(() => api.get('/api/users').catch(() => []), []);
  const locations = useAsync(() => api.get('/api/routing/locations').catch(() => []), []);
  const properties = useAsync(() => api.get('/api/routing/properties').catch(() => []), []);
  // Bewusst ohne Einschränkung auf den Zuständigen: Eine Tour entsteht häufig
  // gerade aus noch unverteilten Aufträgen. Gefiltert wird erst in der Auswahl.
  const candidates = useAsync(() => api.get('/api/routing/candidates', { date: planDate }), [planDate]);
  const plans = useAsync(() => api.get('/api/routing/plans'), [savedPlanId]);

  const startStop = stops.find((stop) => stop.isStart) ?? null;
  const endStop = stops.find((stop) => stop.isEnd) ?? null;
  /** Die eigentlichen Arbeitsstopps – ohne Start- und Zielpunkt. */
  const middleStops = stops.filter((stop) => !stop.isStart && !stop.isEnd);
  const canDispatch = can('dispatch.assign');

  /** Servicestandort als Stopp – für Start- und Zielauswahl. */
  const locationStop = useCallback(
    (location: any, role: 'start' | 'end'): PlannerStop => ({
      key: nextKey(role),
      label: location.name,
      address:
        [
          [location.street, location.house_no].filter(Boolean).join(' '),
          [location.zip, location.city].filter(Boolean).join(' '),
        ]
          .filter(Boolean)
          .join(', ') || null,
      lat: location.latitude,
      lng: location.longitude,
      kind: role === 'start' ? 'start' : 'end',
      isStart: role === 'start',
      isEnd: role === 'end',
      meta: LOCATION_LABEL[location.kind] ?? 'Servicestandort',
      locationId: location.id,
    }),
    [],
  );

  /**
   * Vorbelegung aus dem Standardstandort des gewählten Mitarbeiters.
   *
   * Nur solange Start und Ziel nicht von Hand gesetzt wurden – eine bewusste
   * Auswahl darf ein Wechsel des Zuständigen nicht überschreiben.
   */
  useEffect(() => {
    if (!assigneeId || !locations.data?.length) return;
    const person = (users.data ?? []).find((entry: any) => entry.id === assigneeId);
    const location =
      locations.data.find((entry: any) => entry.id === person?.default_location_id) ??
      locations.data.find((entry: any) => entry.is_default);
    if (!location || location.latitude == null) return;

    setStops((current) => {
      const manualStart = current.find((stop) => stop.isStart && !stop.locationId);
      if (manualStart) return current;
      const withoutAuto = current.filter((stop) => !(stop.isStart && stop.locationId));
      return [locationStop(location, 'start'), ...withoutAuto];
    });
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeId, locations.data, users.data]);

  /* ------------------------------ Stopps --------------------------- */

  /** Prüft, ob ein Stopp bereits in der Tour steht. */
  const isDuplicate = (existing: PlannerStop[], stop: PlannerStop) =>
    existing.some(
      (entry) =>
        (stop.taskId && entry.taskId === stop.taskId) ||
        (!stop.taskId && stop.propertyId && entry.propertyId === stop.propertyId && entry.kind === 'property'),
    );

  const addStop = useCallback((stop: PlannerStop) => {
    setStops((current) => (isDuplicate(current, stop) ? current : [...current, stop]));
    setResult(null);
  }, []);

  /**
   * Mehrere Stopps auf einmal – für die Sammelübernahme.
   *
   * Einzeln aufgerufen würde jeder Aufruf denselben Ausgangszustand sehen und
   * nur der letzte überleben; deshalb wird die ganze Liste in einem Durchgang
   * angehängt und dabei auch gegen Dubletten innerhalb der Liste geprüft.
   */
  const addStops = useCallback((incoming: PlannerStop[]) => {
    setStops((current) => {
      const next = [...current];
      for (const stop of incoming) if (!isDuplicate(next, stop)) next.push(stop);
      return next;
    });
    setResult(null);
  }, []);

  const removeStop = (key: string) => {
    setStops((current) => current.filter((stop) => stop.key !== key));
    setResult(null);
  };

  const setAsStart = (key: string) => {
    setStops((current) =>
      current.map((stop) => ({ ...stop, isStart: stop.key === key, isEnd: stop.key === key ? false : stop.isEnd })),
    );
    setResult(null);
  };

  const setAsEnd = (key: string) => {
    setStops((current) =>
      current.map((stop) => ({ ...stop, isEnd: stop.key === key, isStart: stop.key === key ? false : stop.isStart })),
    );
    setResult(null);
  };

  const move = (key: string, direction: -1 | 1) => {
    setStops((current) => {
      const index = current.findIndex((stop) => stop.key === key);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setResult(null);
  };

  /* --------------------------- Optimierung ------------------------- */

  const optimize = async () => {
    if (stops.length < 2) {
      notify('Für eine Tour werden mindestens zwei Stopps benötigt.', 'error');
      return;
    }
    setOptimizing(true);
    try {
      const payload = await api.post('/api/routing/optimize', {
        stops: stops.map(toApiStop),
        mode,
        avgSpeedKmh: avgSpeed,
        serviceMinutes,
        startTime: startTime ? new Date(`${planDate}T${startTime}:00`).toISOString() : null,
      });
      setResult(payload);
      notify(`Reihenfolge optimiert – ${formatNumber(payload.totalDistanceKm, 2)} km Fahrstrecke.`, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Optimierung fehlgeschlagen.', 'error');
    } finally {
      setOptimizing(false);
    }
  };

  // Ändert sich ein Parameter, ist das vorhandene Ergebnis überholt.
  useEffect(() => setResult(null), [mode, avgSpeed, serviceMinutes, startTime, planDate]);

  // Der Vorschlagsname folgt dem Datum, solange er nicht von Hand geändert wurde.
  useEffect(() => {
    setName((current) => (/^Tour \d{4}-\d{2}-\d{2}$/.test(current) ? `Tour ${planDate}` : current));
  }, [planDate]);

  const save = async (release: boolean) => {
    if (!result) {
      notify('Bitte zuerst die Reihenfolge optimieren.', 'error');
      return;
    }
    if (release && !assigneeId) {
      notify('Für die Übergabe wird ein zuständiger Mitarbeiter benötigt.', 'error');
      return;
    }
    setSaving(true);
    try {
      const plan = await api.post('/api/routing/plans', {
        name,
        planDate,
        assigneeId: assigneeId || null,
        mode,
        avgSpeedKmh: avgSpeed,
        serviceMinutes,
        startTime: startTime ? new Date(`${planDate}T${startTime}:00`).toISOString() : null,
        status: 'draft',
        stops: result.stops.map(toApiStop),
      });
      setSavedPlanId(plan.id);

      if (release) {
        await api.post(`/api/routing/plans/${plan.id}/dispatch`);
        notify('Tour gespeichert und an den Außendienst übergeben.', 'success');
      } else {
        notify('Tour gespeichert.', 'success');
      }
      plans.reload();
      candidates.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const mapStops = (result?.stops ?? stops).map((stop: any) => ({
    key: stop.key,
    label: stop.label,
    address: stop.address,
    lat: stop.lat,
    lng: stop.lng,
    distanceFromPrev: stop.distanceFromPrev,
  }));

  const openTaskCount = candidates.data?.tasks?.length ?? 0;
  const plannedTaskIds = new Set(stops.map((stop) => stop.taskId).filter(Boolean));

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconRoute width={22} height={22} />}
        title="Tourenplanung"
        pill="Routenoptimierung"
        tone="accent"
        subtitle="Offene Aufträge zu einer Tagestour bündeln, Reihenfolge optimieren und an den Außendienst übergeben."
        actions={
          <>
            <button className="btn" onClick={() => navigate('/dispatch')}>
              Zur Disposition
            </button>
            <button className="btn accent" onClick={optimize} disabled={optimizing || stops.length < 2}>
              <IconSpark width={15} height={15} />
              {optimizing ? 'Optimiert …' : 'Route optimieren'}
            </button>
          </>
        }
      />

      <Tabs
        active={view}
        onChange={setView}
        tabs={[
          { key: 'planner', label: 'Planung', icon: <IconRoute width={15} height={15} /> },
          { key: 'saved', label: 'Gespeicherte Touren', count: plans.data?.length ?? 0 },
        ]}
      />

      {view === 'saved' ? (
        <SavedPlans plans={plans} users={users.data ?? []} onReload={() => plans.reload()} />
      ) : (
        <div className="route-layout">
          {/* ------------------------- Linke Spalte ------------------------ */}
          <div className="stack">
            <Card title="Rahmen der Tour" subtitle="Tag, Zuständigkeit und Fahrprofil">
              <div className="stack" style={{ gap: 14 }}>
                <Field label="Bezeichnung">
                  <input value={name} onChange={(event) => setName(event.target.value)} />
                </Field>
                <div className="form-grid">
                  <Field label="Datum">
                    <input type="date" value={planDate} onChange={(event) => setPlanDate(event.target.value)} />
                  </Field>
                  <Field label="Abfahrt">
                    <input type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} />
                  </Field>
                </div>
                <Field label="Zuständig" hint="Nur mit Zuweisung lässt sich die Tour übergeben.">
                  <SearchSelect
                    value={String(assigneeId ?? '')}
                    onChange={(wert) => setAssigneeId(wert ? Number(wert) : '')}
                    options={(users.data ?? []).map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
                    placeholder="Noch offen"
                  />
                </Field>

                <div className="form-grid">
                  <Field label={`Ø Tempo: ${avgSpeed} km/h`} hint="Ortsverkehr inkl. Umwegfaktor">
                    <input
                      type="range"
                      min={10}
                      max={90}
                      value={avgSpeed}
                      onChange={(event) => setAvgSpeed(Number(event.target.value))}
                    />
                  </Field>
                  <Field label={`Standzeit: ${serviceMinutes} min`} hint="Vorgabe je Stopp">
                    <input
                      type="range"
                      min={0}
                      max={180}
                      step={5}
                      value={serviceMinutes}
                      onChange={(event) => setServiceMinutes(Number(event.target.value))}
                    />
                  </Field>
                </div>
              </div>
            </Card>

            <Card title="Start- und Zielpunkt" subtitle="Servicestandort, Objekt oder freie Adresse">
              <div className="stack" style={{ gap: 16 }}>
                <div className="stack" style={{ gap: 8 }}>
                  <span className="section-title">Startpunkt</span>
                  {startStop ? (
                    <div className="route-stop is-start">
                      <span className="seq">
                        <IconPin width={15} height={15} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className="stop-title truncate">{startStop.label}</div>
                        <div className="stop-meta truncate">{startStop.meta ?? startStop.address ?? '–'}</div>
                      </div>
                      <button className="btn sm ghost" title="Startpunkt entfernen" onClick={() => removeStop(startStop.key)}>
                        <IconX width={13} height={13} />
                      </button>
                    </div>
                  ) : (
                    <PointPicker
                      role="start"
                      locations={locations.data ?? []}
                      properties={properties.data ?? []}
                      locationStop={locationStop}
                      onPick={addStop}
                    />
                  )}
                </div>

                <div className="stack" style={{ gap: 8 }}>
                  <span className="section-title">Zielpunkt</span>
                  <div className="btn-group" style={{ width: '100%' }}>
                    <button
                      className={`btn sm ${endChoice === 'start' ? 'active' : ''}`}
                      style={{ flex: 1 }}
                      onClick={() => {
                        setEndChoice('start');
                        if (endStop) removeStop(endStop.key);
                      }}
                    >
                      Zurück zum Start
                    </button>
                    <button
                      className={`btn sm ${endChoice === 'open' ? 'active' : ''}`}
                      style={{ flex: 1 }}
                      onClick={() => {
                        setEndChoice('open');
                        if (endStop) removeStop(endStop.key);
                      }}
                    >
                      Offenes Ende
                    </button>
                    <button
                      className={`btn sm ${endChoice === 'fixed' ? 'active' : ''}`}
                      style={{ flex: 1 }}
                      onClick={() => setEndChoice('fixed')}
                    >
                      Fester Zielpunkt
                    </button>
                  </div>

                  {endChoice === 'fixed' &&
                    (endStop ? (
                      <div className="route-stop is-end">
                        <span className="seq">
                          <IconNavigation width={15} height={15} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div className="stop-title truncate">{endStop.label}</div>
                          <div className="stop-meta truncate">{endStop.meta ?? endStop.address ?? '–'}</div>
                        </div>
                        <button className="btn sm ghost" title="Zielpunkt entfernen" onClick={() => removeStop(endStop.key)}>
                          <IconX width={13} height={13} />
                        </button>
                      </div>
                    ) : (
                      <PointPicker
                        role="end"
                        locations={locations.data ?? []}
                        properties={properties.data ?? []}
                        locationStop={locationStop}
                        onPick={addStop}
                      />
                    ))}

                  {endChoice === 'start' && startStop && (
                    <span className="small subtle">Die Tour endet wieder an „{startStop.label}“.</span>
                  )}
                  {endChoice === 'open' && (
                    <span className="small subtle">
                      Der letzte Stopp ergibt sich aus der Optimierung – geeignet, wenn die Fahrt zu Hause endet.
                    </span>
                  )}
                </div>
              </div>
            </Card>

            <Card
              title={`Stopps (${middleStops.length})`}
              subtitle="Aufträge, Objekte und freie Adressen"
              actions={
                <button className="btn sm primary" onClick={() => setTaskPickerOpen(true)}>
                  <IconPlus width={14} height={14} /> Aufträge
                </button>
              }
            >
              <div className="stack" style={{ gap: 10 }}>
                {middleStops.length === 0 && (
                  <EmptyState
                    icon={<IconPin width={20} height={20} />}
                    title="Noch keine Stopps"
                    hint={`Aus ${openTaskCount} offenen Aufträgen mit verortetem Objekt auswählen.`}
                  />
                )}

                {middleStops.map((stop) => (
                    <div
                      key={stop.key}
                      className={`route-stop ${selectedKey === stop.key ? 'selected' : ''}`}
                      onClick={() => setSelectedKey(stop.key === selectedKey ? null : stop.key)}
                    >
                      <span className="seq" style={stop.criticalityColor ? { background: stop.criticalityColor } : undefined}>
                        {stop.kind === 'task' ? <IconCheck width={14} height={14} /> : <IconBuilding width={14} height={14} />}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className="stop-title truncate">{stop.label}</div>
                        <div className="stop-meta truncate">{stop.meta ?? stop.address ?? '–'}</div>
                      </div>
                      <div className="row" style={{ gap: 2, flexWrap: 'nowrap' }}>
                        <button className="btn sm ghost icon-only" title="Nach oben" onClick={() => move(stop.key, -1)}>
                          ↑
                        </button>
                        <button className="btn sm ghost icon-only" title="Nach unten" onClick={() => move(stop.key, 1)}>
                          ↓
                        </button>
                        <button
                          className="btn sm ghost icon-only"
                          title="Als Zielpunkt setzen"
                          onClick={() => {
                            setEndChoice('fixed');
                            setAsEnd(stop.key);
                          }}
                        >
                          <IconNavigation width={13} height={13} />
                        </button>
                        <button
                          className="btn sm ghost icon-only"
                          title="Als Startpunkt setzen"
                          onClick={() => setAsStart(stop.key)}
                        >
                          <IconPin width={13} height={13} />
                        </button>
                        <button className="btn sm ghost icon-only" title="Entfernen" onClick={() => removeStop(stop.key)}>
                          <IconTrash width={13} height={13} />
                        </button>
                      </div>
                    </div>
                  ))}

                <div className="stack" style={{ gap: 8, marginTop: 4 }}>
                  <span className="section-title">Weiteren Stopp hinzufügen</span>
                  <SearchSelect
                    value={''}
                    onChange={(wert) => {
                    const property = (properties.data ?? []).find((entry: any) => entry.id === Number(wert));
                    if (!property) return;
                    addStop({
                    key: nextKey('prop'),
                    label: property.name,
                    address: [property.street, `${property.zip ?? ''} ${property.city ?? ''}`.trim()]
                    .filter(Boolean)
                    .join(', '),
                    lat: property.latitude,
                    lng: property.longitude,
                    kind: 'property',
                    propertyId: property.id,
                    meta: `Objekt · ${property.open_tasks ?? 0} offene Vorgänge`,
                    });
                    wert = '';
                    }}
                    options={(properties.data ?? []).map((property: any) => ({ value: String(property.id), label: `${property.name} · ${property.city}` }))}
                    placeholder="Objekt auswählen …"
                  />
                  <AddressSearch
                    placeholder="Freie Adresse suchen …"
                    onPick={(hit) =>
                      addStop({
                        key: nextKey('addr'),
                        label: hit.label,
                        address: hit.address,
                        lat: hit.lat,
                        lng: hit.lng,
                        kind: 'address',
                        meta: 'Freie Adresse',
                      })
                    }
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* ------------------------ Rechte Spalte ------------------------ */}
          <div className="stack">
            <div className="grid cols-4">
              <StatCard
                label="Fahrstrecke"
                value={result ? `${formatNumber(result.totalDistanceKm, 2)} km` : '–'}
                tone="primary"
                icon={<IconTruck width={17} height={17} />}
                hint={result?.returnLeg ? `inkl. ${formatNumber(result.returnLeg.distanceKm, 2)} km Rückweg` : 'Luftlinie × 1,32'}
              />
              <StatCard
                label="Fahrzeit"
                value={result ? formatDuration(result.totalDriveMinutes) : '–'}
                tone="info"
                icon={<IconClock width={17} height={17} />}
                hint={`bei Ø ${avgSpeed} km/h`}
              />
              <StatCard
                label="Tourdauer"
                value={result ? formatDuration(result.totalDurationMinutes) : '–'}
                tone="accent"
                icon={<IconRoute width={17} height={17} />}
                hint={result ? `davon ${formatDuration(result.totalServiceMinutes)} vor Ort` : 'Fahrt + Standzeiten'}
              />
              <StatCard
                label="Stopps"
                value={middleStops.length}
                tone="success"
                icon={<IconPin width={17} height={17} />}
                hint={result?.stops.length ? `Rückkehr ${lastArrival(result)}` : `${openTaskCount} Aufträge verfügbar`}
              />
            </div>

            <RouteMap
              stops={mapStops}
              roundTrip={mode === 'round_trip'}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
            />

            <Card
              title="Optimierte Reihenfolge"
              subtitle={
                result
                  ? `${result.stops.length} Stopps · ${formatNumber(result.totalDistanceKm, 2)} km · ${formatDuration(result.totalDurationMinutes)}`
                  : 'Noch nicht berechnet'
              }
              actions={
                result && (
                  <>
                    <a className="btn sm" href={result.mapsUrl} target="_blank" rel="noreferrer">
                      <IconLink width={14} height={14} /> Google Maps
                    </a>
                    <button className="btn sm" onClick={() => save(false)} disabled={saving}>
                      <IconSave width={14} height={14} /> Speichern
                    </button>
                    {canDispatch && (
                      <button className="btn sm primary" onClick={() => save(true)} disabled={saving || !assigneeId}>
                        <IconCheck width={14} height={14} /> Übergeben
                      </button>
                    )}
                  </>
                )
              }
            >
              {!result ? (
                <EmptyState
                  icon={<IconSpark width={20} height={20} />}
                  title="Reihenfolge noch nicht berechnet"
                  hint="Stopps sammeln und anschließend die Optimierung starten. Bis zu neun Stopps werden exakt gelöst, darüber hinaus per 2-Opt-Heuristik."
                  action={
                    <button className="btn accent" onClick={optimize} disabled={stops.length < 2}>
                      <IconSpark width={15} height={15} /> Route optimieren
                    </button>
                  }
                />
              ) : (
                <div className="stack" style={{ gap: 0 }}>
                  {result.stops.map((stop, index) => (
                    <div key={stop.key}>
                      {index > 0 && (
                        <div className="route-leg">
                          <IconTruck width={13} height={13} />
                          {formatNumber(stop.distanceFromPrev, 2)} km · {formatDuration(stop.driveMinutesFromPrev)}
                        </div>
                      )}
                      <div
                        className={`route-stop ${selectedKey === stop.key ? 'selected' : ''} ${index === 0 ? 'is-start' : ''} ${
                          index === result.stops.length - 1 && mode !== 'round_trip' ? 'is-end' : ''
                        }`}
                        onClick={() => setSelectedKey(stop.key === selectedKey ? null : stop.key)}
                      >
                        <span className="seq">{index + 1}</span>
                        <div style={{ minWidth: 0 }}>
                          <div className="stop-title truncate">{stop.label}</div>
                          <div className="stop-meta truncate">{stop.address ?? stop.meta ?? '–'}</div>
                        </div>
                        <div className="text-right">
                          <div className="small" style={{ fontWeight: 700 }}>
                            {stop.plannedArrival ? formatTime(stop.plannedArrival) : '–'}
                          </div>
                          <div className="small subtle">
                            {index === 0 ? 'Abfahrt' : `${stop.serviceMinutes ?? serviceMinutes} min vor Ort`}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {result.returnLeg && (
                    <div className="route-leg">
                      <IconTruck width={13} height={13} />
                      Rückweg: {formatNumber(result.returnLeg.distanceKm, 2)} km ·{' '}
                      {formatDuration(result.returnLeg.driveMinutes)}
                    </div>
                  )}

                  <div className="note-box tone-success" style={{ marginTop: 14 }}>
                    <IconCheck width={16} height={16} />
                    <span>
                      <strong>Auslastung:</strong> {formatDuration(result.totalDurationMinutes)} von 8 h Regelarbeitszeit.
                      <Meter
                        value={result.totalDurationMinutes}
                        max={480}
                        tone={result.totalDurationMinutes > 480 ? 'danger' : 'success'}
                      />
                    </span>
                  </div>
                </div>
              )}
            </Card>

            {candidates.data?.withoutLocation?.length > 0 && (
              <div className="alert warning">
                <IconPin width={16} height={16} />
                <span>
                  {candidates.data.withoutLocation.length} offene Aufträge lassen sich nicht verplanen, weil das Objekt
                  keine Koordinaten hat. Ergänzen Sie diese im Objektstamm –{' '}
                  {candidates.data.withoutLocation
                    .slice(0, 3)
                    .map((task: any) => task.task_no)
                    .join(', ')}
                  {candidates.data.withoutLocation.length > 3 ? ' …' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {taskPickerOpen && (
        <TaskPicker
          loading={candidates.loading}
          tasks={candidates.data?.tasks ?? []}
          plannedTaskIds={plannedTaskIds}
          assigneeId={assigneeId || null}
          users={users.data ?? []}
          onClose={() => setTaskPickerOpen(false)}
          onPick={(task) => addStop(taskToStop(task))}
          onPickMany={(list) => {
            addStops(list.map(taskToStop));
            notify(`${list.length} Aufträge übernommen.`, 'success');
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bausteine                                                           */
/* ------------------------------------------------------------------ */

/** Wandelt einen Auftrag in einen Tourenstopp. */
function taskToStop(task: any): PlannerStop {
  return {
    key: nextKey('task'),
    label: `${task.task_no} · ${task.title}`,
    address: [task.street, `${task.zip ?? ''} ${task.city ?? ''}`.trim()].filter(Boolean).join(', '),
    lat: task.latitude,
    lng: task.longitude,
    kind: 'task',
    taskId: task.id,
    propertyId: task.property_id,
    serviceMinutes: task.estimated_minutes ?? null,
    meta: `${task.property_name}${task.location_detail ? ` · ${task.location_detail}` : ''}`,
    criticalityColor: task.criticality_color,
    criticalityName: task.criticality_name,
  };
}

/** Reduziert einen Planungsstopp auf die Felder, die der Server erwartet. */
function toApiStop(stop: PlannerStop) {
  return {
    key: stop.key,
    label: stop.label,
    address: stop.address,
    lat: stop.lat,
    lng: stop.lng,
    kind: stop.kind,
    taskId: stop.taskId ?? null,
    propertyId: stop.propertyId ?? null,
    serviceMinutes: stop.serviceMinutes ?? null,
    isStart: stop.isStart ?? false,
    isEnd: stop.isEnd ?? false,
  };
}

function lastArrival(result: OptimizeResult): string {
  const last = result.stops[result.stops.length - 1];
  if (!last?.plannedDeparture) return '–';
  const end = new Date(last.plannedDeparture);
  if (result.returnLeg) end.setMinutes(end.getMinutes() + result.returnLeg.driveMinutes);
  return end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Auswahl eines Start- oder Zielpunktes.
 *
 * Servicestandorte stehen obenan – sie sind der Regelfall und tragen bereits
 * Koordinaten. Objekte und freie Adressen bleiben als Ausnahme möglich.
 */
function PointPicker({
  role,
  locations,
  properties,
  locationStop,
  onPick,
}: {
  role: 'start' | 'end';
  locations: any[];
  properties: any[];
  locationStop: (location: any, role: 'start' | 'end') => PlannerStop;
  onPick: (stop: PlannerStop) => void;
}) {
  const usable = locations.filter((location) => location.latitude != null && location.longitude != null);

  return (
    <div className="stack" style={{ gap: 8 }}>
      {usable.length > 0 && (
        <SearchSelect
          value={''}
          onChange={(wert) => {
          const location = usable.find((entry) => entry.id === Number(wert));
          if (location) onPick(locationStop(location, role));
          wert = '';
          }}
          options={(usable ?? []).map((location: any) => ({ value: String(location.id), label: `${location.name} ${location.is_default ? ' (Vorgabe)' : ''} ${location.city ? ` · ${location.city}` : ''}` }))}
          placeholder="Servicestandort wählen …"
        />
      )}

      <SearchSelect
        value={''}
        onChange={(wert) => {
        const property = properties.find((entry: any) => entry.id === Number(wert));
        if (!property) return;
        onPick({
        key: nextKey(role),
        label: property.name,
        address: [property.street, `${property.zip ?? ''} ${property.city ?? ''}`.trim()]
        .filter(Boolean)
        .join(', '),
        lat: property.latitude,
        lng: property.longitude,
        kind: role === 'start' ? 'start' : 'end',
        propertyId: property.id,
        isStart: role === 'start',
        isEnd: role === 'end',
        meta: 'Objekt',
        });
        wert = '';
        }}
        options={(properties ?? []).map((property: any) => ({ value: String(property.id), label: String(property.name) }))}
        placeholder="… oder ein Objekt"
      />

      <AddressSearch
        placeholder={role === 'start' ? '… oder Startadresse suchen' : '… oder Zieladresse suchen'}
        onPick={(hit) =>
          onPick({
            key: nextKey(role),
            label: hit.label,
            address: hit.address,
            lat: hit.lat,
            lng: hit.lng,
            kind: role === 'start' ? 'start' : 'end',
            isStart: role === 'start',
            isEnd: role === 'end',
            meta: 'Freie Adresse',
          })
        }
      />
    </div>
  );
}

/** Adresssuche mit Vorschlägen aus dem serverseitigen Geokodierer. */
function AddressSearch({
  placeholder,
  onPick,
  extra,
}: {
  placeholder: string;
  onPick: (hit: { label: string; address: string; lat: number; lng: number }) => void;
  extra?: JSX.Element;
}) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number>();

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (query.trim().length < 3) {
      setHits([]);
      return;
    }
    // Nominatim bittet um sparsame Abfragen – daher erst nach einer Pause.
    timer.current = window.setTimeout(async () => {
      setBusy(true);
      setError(null);
      try {
        setHits(await api.get('/api/routing/geocode', { q: query }));
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Adresssuche nicht verfügbar.');
        setHits([]);
      } finally {
        setBusy(false);
      }
    }, 550);
    return () => window.clearTimeout(timer.current);
  }, [query]);

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div style={{ position: 'relative' }}>
        <input
          type="search"
          value={query}
          placeholder={placeholder}
          onChange={(event) => setQuery(event.target.value)}
          style={{ paddingLeft: 34 }}
        />
        <span style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-subtle)' }}>
          <IconSearch width={15} height={15} />
        </span>
        {hits.length > 0 && (
          <div className="suggestion-list">
            {hits.map((hit, index) => (
              <button
                key={`${hit.lat}-${hit.lng}-${index}`}
                onClick={() => {
                  onPick(hit);
                  setQuery('');
                  setHits([]);
                }}
              >
                {hit.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {busy && <span className="small subtle">Adresse wird gesucht …</span>}
      {error && <span className="small" style={{ color: 'var(--warning-text)' }}>{error}</span>}
      {extra}
    </div>
  );
}

/** Auswahl offener Aufträge, die in die Tour übernommen werden. */
function TaskPicker({
  loading,
  tasks,
  plannedTaskIds,
  assigneeId,
  users,
  onPick,
  onPickMany,
  onClose,
}: {
  loading: boolean;
  tasks: any[];
  plannedTaskIds: Set<number | null | undefined>;
  assigneeId: number | null;
  users: any[];
  onPick: (task: any) => void;
  onPickMany: (tasks: any[]) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [onlyEmergency, setOnlyEmergency] = useState(false);
  /**
   * Mitarbeiterfilter. `''` zeigt alles, `'unassigned'` nur Unverteiltes,
   * eine Zahl die Aufträge dieser Person. Vorbelegt mit dem Zuständigen der
   * Tour – das ist der häufigste Fall.
   */
  const [personFilter, setPersonFilter] = useState<string>(assigneeId ? String(assigneeId) : '');

  const filtered = useMemo(
    () =>
      tasks.filter((task) => {
        if (personFilter === 'unassigned' && task.assignee_id != null) return false;
        if (personFilter && personFilter !== 'unassigned') {
          // Unverteilte Aufträge bleiben sichtbar: Auch sie gehören auf die
          // Tour dieses Mitarbeitenden, sie sind nur noch nicht zugewiesen.
          if (task.assignee_id != null && task.assignee_id !== Number(personFilter)) return false;
        }
        if (onlyEmergency && task.criticality_level !== 1) return false;
        if (!search.trim()) return true;
        const haystack = `${task.task_no} ${task.title} ${task.property_name ?? ''} ${task.city ?? ''}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      }),
    [tasks, search, personFilter, onlyEmergency],
  );

  /** Noch nicht eingeplante Treffer – nur die lassen sich übernehmen. */
  const addable = filtered.filter((task) => !plannedTaskIds.has(task.id));

  return (
    <Modal
      title="Offene Aufträge übernehmen"
      size="wide"
      onClose={onClose}
      footer={
        <button className="btn primary" onClick={onClose}>
          Fertig
        </button>
      }
    >
      <div className="stack">
        <div className="row">
          <input
            type="search"
            placeholder="Nummer, Titel oder Objekt suchen …"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <select
            value={personFilter}
            onChange={(event) => setPersonFilter(event.target.value)}
            style={{ width: 'auto', minWidth: 210 }}
          >
            <option value="">Alle Mitarbeitenden</option>
            <option value="unassigned">Nur unverteilte Aufträge</option>
            {users.map((person: any) => (
              <option key={person.id} value={person.id}>
                {person.first_name} {person.last_name}
              </option>
            ))}
          </select>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={onlyEmergency}
              onChange={(event) => setOnlyEmergency(event.target.checked)}
            />
            Nur Notfälle
          </label>
        </div>

        <div className="row between">
          <span className="small subtle">
            {filtered.length} Treffer
            {addable.length !== filtered.length && ` · ${filtered.length - addable.length} bereits eingeplant`}
            {personFilter && personFilter !== 'unassigned' && ' · unverteilte Aufträge sind eingeschlossen'}
          </span>
          <button
            className="btn sm primary"
            disabled={addable.length === 0}
            onClick={() => onPickMany(addable)}
            title="Alle Treffer der aktuellen Filterung in die Tour übernehmen"
          >
            <IconPlus width={13} height={13} /> Alle übernehmen ({addable.length})
          </button>
        </div>

        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <EmptyState title="Keine passenden Aufträge" hint="Alle verorteten Aufträge sind bereits eingeplant." />
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {filtered.map((task) => {
              const planned = plannedTaskIds.has(task.id);
              return (
                <div
                  key={task.id}
                  className={`order-card ${task.criticality_level === 1 ? 'tone-danger' : task.criticality_level === 2 ? 'tone-warning' : 'tone-primary'}`}
                  style={{ padding: '12px 18px' }}
                >
                  <div className="row between">
                    <div className="row" style={{ gap: 8 }}>
                      <Badge color={task.criticality_color}>{task.criticality_name ?? 'Ohne Stufe'}</Badge>
                      <span className="mono subtle">{task.task_no}</span>
                      {task.category_name && <span className="chip">{task.category_name}</span>}
                    </div>
                    <button
                      className={`btn sm ${planned ? 'soft' : 'primary'}`}
                      disabled={planned}
                      onClick={() => onPick(task)}
                    >
                      {planned ? 'Eingeplant' : 'Hinzufügen'}
                    </button>
                  </div>
                  <div className="order-card-title" style={{ fontSize: 14 }}>
                    {task.title}
                  </div>
                  <div className="small subtle">
                    <IconPin width={12} height={12} /> {task.property_name}
                    {task.location_detail ? ` · ${task.location_detail}` : ''} ·{' '}
                    {task.assignee_first_name
                      ? `${task.assignee_first_name} ${task.assignee_last_name}`
                      : 'nicht zugewiesen'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Übersicht der gespeicherten Touren inkl. Freigabe und Löschen. */
function SavedPlans({
  plans,
  users,
  onReload,
}: {
  plans: ReturnType<typeof useAsync<any[]>>;
  users: any[];
  onReload: () => void;
}) {
  const { notify } = useToast();
  const { can } = useAuth();
  /** Abweichende Zuweisung je Tour, solange sie noch nicht übergeben ist. */
  const [chosen, setChosen] = useState<Record<number, number | ''>>({});

  if (plans.loading) return <Loading />;
  if (!plans.data?.length) {
    return (
      <Card>
        <EmptyState
          icon={<IconRoute width={20} height={20} />}
          title="Noch keine Tour gespeichert"
          hint="Eine geplante Tour lässt sich sichern und später erneut übergeben."
        />
      </Card>
    );
  }

  const remove = async (id: number) => {
    await api.delete(`/api/routing/plans/${id}`);
    notify('Tour gelöscht.', 'success');
    onReload();
  };

  const release = async (plan: any) => {
    const assigneeId = chosen[plan.id] || plan.assignee_id || null;
    if (!assigneeId) {
      notify('Bitte zuerst den zuständigen Mitarbeitenden wählen.', 'error');
      return;
    }
    try {
      const result = await api.post(`/api/routing/plans/${plan.id}/dispatch`, { assigneeId });
      notify(
        result.reassignedCount > 0
          ? `Tour übergeben – ${result.reassignedCount} Aufträge von anderen Mitarbeitenden umdisponiert.`
          : 'Tour übergeben – Aufträge wurden zugewiesen.',
        'success',
      );
      onReload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Übergabe fehlgeschlagen.', 'error');
    }
  };

  return (
    <div className="stack" style={{ gap: 10 }}>
      {plans.data.map((plan: any) => (
        <SavedPlanCard
          key={plan.id}
          plan={plan}
          users={users}
          canAssign={can('dispatch.assign')}
          chosenAssignee={chosen[plan.id]}
          onChooseAssignee={(value) => setChosen((current) => ({ ...current, [plan.id]: value }))}
          onRelease={() => release(plan)}
          onRemove={() => remove(plan.id)}
        />
      ))}
    </div>
  );
}

/**
 * Eine gespeicherte Tour als Kachel – zugeklappt die Eckdaten, aufgeklappt
 * die Haltepunkte in der geplanten Reihenfolge samt Teilstrecken.
 *
 * Die Stopps werden erst beim Aufklappen geladen: Die Übersicht liefert nur
 * Kennzahlen, und bei zwanzig gespeicherten Touren wäre es Verschwendung,
 * jede Reihenfolge im Voraus zu holen. Einmal geladen, bleibt sie im
 * Zustand – erneutes Auf- und Zuklappen kostet keine weitere Abfrage.
 */
function SavedPlanCard({
  plan,
  users,
  canAssign,
  chosenAssignee,
  onChooseAssignee,
  onRelease,
  onRemove,
}: {
  plan: any;
  users: any[];
  canAssign: boolean;
  chosenAssignee: number | '' | undefined;
  onChooseAssignee: (value: number | '') => void;
  onRelease: () => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || detail || loading) return;

    setLoading(true);
    setError(null);
    try {
      setDetail(await api.get(`/api/routing/plans/${plan.id}`));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Die Reihenfolge ließ sich nicht laden.');
    } finally {
      setLoading(false);
    }
  };

  const statusLabel =
    plan.status === 'released' ? 'Übergeben' : plan.status === 'done' ? 'Abgeschlossen' : 'Entwurf';
  const stops: any[] = detail?.stops ?? [];

  return (
    <div className={`order-card plan-tile ${plan.status === 'released' ? 'tone-success' : 'tone-primary'}`}>
      {/* Kopfzeile: klickbar auf ganzer Breite, damit der Aufklapper leicht zu treffen ist. */}
      <button className="plan-tile-head" onClick={toggle} aria-expanded={open}>
        <IconChevronDown
          width={16}
          height={16}
          style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms', flexShrink: 0 }}
        />

        <div className="plan-tile-main">
          <div className="row" style={{ gap: 8 }}>
            <strong>{plan.name}</strong>
            <Badge variant={plan.status === 'released' ? 'success' : plan.status === 'done' ? '' : 'warning'}>
              {statusLabel}
            </Badge>
            <span className="chip">{MODE_LABELS[plan.mode as Mode] ?? plan.mode}</span>
          </div>
          <div className="small subtle">
            {plan.plan_date ? formatDate(plan.plan_date) : 'ohne Datum'} · {plan.stop_count} Stopps ·{' '}
            {formatNumber(plan.total_distance_km, 2)} km ·{' '}
            {plan.assignee_first_name ? `${plan.assignee_first_name} ${plan.assignee_last_name}` : 'niemand zugewiesen'}
          </div>
        </div>

        <span className="plan-tile-figure">
          <strong>{formatNumber(plan.total_distance_km, 2)}</strong>
          <span>km</span>
        </span>
      </button>

      {open && (
        <div className="plan-tile-body">
          {loading && <Loading />}
          {error && <div className="alert error">{error}</div>}

          {!loading && !error && stops.length === 0 && (
            <span className="small subtle">Diese Tour enthält keine Haltepunkte.</span>
          )}

          {stops.map((stop, index) => (
            <div key={stop.id ?? index}>
              {index > 0 && stop.distance_from_prev > 0 && (
                <div className="route-leg">
                  <IconTruck width={13} height={13} />
                  {formatNumber(stop.distance_from_prev, 2)} km
                </div>
              )}
              <div
                className={`route-stop ${index === 0 ? 'is-start' : ''} ${
                  index === stops.length - 1 && plan.mode !== 'round_trip' ? 'is-end' : ''
                }`}
              >
                <span className="seq">{index === 0 ? <IconPin width={14} height={14} /> : index}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="stop-title truncate">{stop.task_title ?? stop.label}</div>
                  <div className="stop-meta truncate">
                    {stop.property_name ?? stop.address ?? '–'}
                    {stop.task_no ? ` · ${stop.task_no}` : ''}
                  </div>
                </div>
                <div className="text-right" style={{ flexShrink: 0 }}>
                  {stop.planned_arrival && <div className="small" style={{ fontWeight: 700 }}>{formatTime(stop.planned_arrival)}</div>}
                  {stop.criticality_name && (
                    <Badge color={stop.criticality_color}>{stop.criticality_name}</Badge>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Rückweg der Rundtour ist kein eigener Stopp, gehört aber zur Strecke. */}
          {plan.mode === 'round_trip' && stops.length > 2 && (
            <div className="route-leg">
              <IconTruck width={13} height={13} /> Rückweg zum Startpunkt
            </div>
          )}
        </div>
      )}

      {/* Zuweisung erst bei der Übergabe – eine gespeicherte Tour ohne
          Zuständigen wäre sonst nicht mehr verwendbar. */}
      {canAssign && plan.status === 'draft' && (
        <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: 'nowrap' }}>
          <SearchSelect
            value={String(chosenAssignee ?? plan.assignee_id ?? '')}
            onChange={(wert) => onChooseAssignee(wert ? Number(wert) : '')}
            options={(users ?? []).map((person: any) => ({ value: String(person.id), label: `${person.first_name} ${person.last_name}` }))}
            placeholder="Mitarbeitenden wählen …"
            style={{ flex: 1 }}
          />
        </div>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        {plan.maps_url && (
          <a className="btn sm" href={plan.maps_url} target="_blank" rel="noreferrer">
            <IconLink width={14} height={14} /> Navigation
          </a>
        )}
        {canAssign && plan.status === 'draft' && (
          <button className="btn sm primary" onClick={onRelease}>
            <IconCheck width={14} height={14} /> Übergeben
          </button>
        )}
        <span className="spacer" />
        <button className="btn sm ghost" onClick={onRemove} title="Tour löschen">
          <IconTrash width={14} height={14} />
        </button>
      </div>
    </div>
  );
}
